import * as Sentry from "@sentry/react";

/**
 * Configure axios interceptors for Sentry tracing
 * This ensures that traces are properly propagated from frontend to backend
 */
export const configureSentryAxiosInterceptor = (axiosInstance) => {
  try {
    // Only configure if Sentry is available
    if (typeof Sentry === "undefined" || !Sentry.getClient()) {
      console.log(
        "Sentry not available, skipping axios interceptor configuration"
      );
      return;
    }

    // Request interceptor to add Sentry trace headers
    axiosInstance.interceptors.request.use(
      (config) => {
        try {
          // Get the current active span
          const span = Sentry.getActiveSpan();
          if (span && typeof Sentry.spanToTraceHeader === "function") {
            // Add trace headers for proper distributed tracing
            const traceHeader = Sentry.spanToTraceHeader(span);
            const baggage = Sentry.spanToBaggageHeader
              ? Sentry.spanToBaggageHeader(span)
              : null;

            if (traceHeader) {
              config.headers["sentry-trace"] = traceHeader;
            }
            if (baggage) {
              config.headers["baggage"] = baggage;
            }
          }
        } catch (error) {
          // Don't break the request if Sentry fails
          console.warn("Error adding Sentry trace headers:", error);
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle Sentry performance monitoring
    axiosInstance.interceptors.response.use(
      (response) => {
        try {
          // Success response - optionally add custom tags or data to the span
          const span = Sentry.getActiveSpan();
          if (span && typeof span.setData === "function") {
            span.setData("http.response.status_code", response.status);
            span.setData(
              "http.response.size",
              response.headers["content-length"]
            );
          }
        } catch (error) {
          console.warn("Error setting Sentry span data:", error);
        }

        return response;
      },
      (error) => {
        try {
          // Error response - report to Sentry with additional context
          const span = Sentry.getActiveSpan();
          if (span && typeof span.setData === "function") {
            span.setData("http.response.status_code", error.response?.status);
            if (typeof span.setStatus === "function") {
              span.setStatus({ code: 2, message: "error" });
            }
          }

          // Capture API errors with additional context
          if (error.response && typeof Sentry.withScope === "function") {
            Sentry.withScope((scope) => {
              scope.setTag("error.type", "api_error");
              scope.setContext("api_error", {
                url: error.config?.url,
                method: error.config?.method,
                status: error.response.status,
                statusText: error.response.statusText,
                data: error.response.data,
              });
              Sentry.captureException(error);
            });
          }
        } catch (sentryError) {
          console.warn("Error capturing error in Sentry:", sentryError);
        }

        return Promise.reject(error);
      }
    );
  } catch (error) {
    console.warn("Error configuring Sentry axios interceptors:", error);
  }
};
